import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Clear = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🧹 CSS Float Clear</h1>

      <p className="tutorial-intro">
        The <code>clear</code> property in CSS is used to control the behavior
        of elements next to floated elements. It ensures that an element moves
        below floated elements instead of wrapping around them.
      </p>

      {/* Why Use Clear */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fcd34d)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h2 style={{ color: "#92400e", marginBottom: "0.5rem" }}>
          🌟 Why Use Clear?
        </h2>
        <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
          When elements are floated left or right, the content after them may
          wrap around. The <code>clear</code> property forces an element to stay
          below floated elements. This is very important in multi-column layouts
          or when working with floated images and text.
        </p>
      </div>

      {/* clear: left */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ clear: left</h2>
        <p>
          Prevents the element from sitting next to any floated element on the{" "}
          <b>left</b> side.
        </p>

        <CodeCopyBlock
          code={`.clear-left {
  clear: left;
  background: #86efac;
  padding: 10px;
}`}
        />

        <div style={{ border: "2px dashed #86efac", padding: "10px" }}>
          <div
            style={{
              float: "left",
              width: "120px",
              height: "80px",
              backgroundColor: "#60a5fa",
              margin: "10px",
              borderRadius: "6px",
            }}
          ></div>
          <div
            style={{
              clear: "left",
              background: "#86efac",
              padding: "10px",
              borderRadius: "6px",
            }}
          >
            This box is pushed below the floated left element.
          </div>
        </div>
      </div>

      {/* clear: right */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ clear: right</h2>
        <p>
          Prevents the element from sitting next to any floated element on the{" "}
          <b>right</b> side.
        </p>

        <CodeCopyBlock
          code={`.clear-right {
  clear: right;
  background: #fca5a5;
  padding: 10px;
}`}
        />

        <div style={{ border: "2px dashed #fca5a5", padding: "10px" }}>
          <div
            style={{
              float: "right",
              width: "120px",
              height: "80px",
              backgroundColor: "#fbbf24",
              margin: "10px",
              borderRadius: "6px",
            }}
          ></div>
          <div
            style={{
              clear: "right",
              background: "#fca5a5",
              padding: "10px",
              borderRadius: "6px",
            }}
          >
            This box is pushed below the floated right element.
          </div>
        </div>
      </div>

      {/* clear: both */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ clear: both</h2>
        <p>
          Prevents the element from sitting next to any floated element on{" "}
          <b>either side</b>.
        </p>

        <CodeCopyBlock
          code={`.clear-both {
  clear: both;
  background: #f9a8d4;
  padding: 10px;
}`}
        />

        <div style={{ border: "2px dashed #f9a8d4", padding: "10px" }}>
          <div
            style={{
              float: "left",
              width: "100px",
              height: "70px",
              backgroundColor: "#34d399",
              margin: "10px",
              borderRadius: "6px",
            }}
          ></div>
          <div
            style={{
              float: "right",
              width: "100px",
              height: "70px",
              backgroundColor: "#60a5fa",
              margin: "10px",
              borderRadius: "6px",
            }}
          ></div>
          <div
            style={{
              clear: "both",
              background: "#f9a8d4",
              padding: "10px",
              borderRadius: "6px",
            }}
          >
            This box is below both floated elements.
          </div>
        </div>
      </div>

      {/* clearfix hack */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ clearfix hack</h2>
        <p>
          Sometimes, floated elements make their parent collapse. The{" "}
          <code>::after</code> pseudo-element can be used to automatically clear
          floats (clearfix technique).
        </p>

        <CodeCopyBlock
          code={`.clearfix::after {
  content: "";
  display: table;
  clear: both;
}`}
        />

        <div
          className="clearfix"
          style={{
            border: "2px dashed #c084fc",
            padding: "10px",
            marginTop: "10px",
          }}
        >
          <div
            style={{
              float: "left",
              width: "100px",
              height: "70px",
              backgroundColor: "#c084fc",
              margin: "10px",
              borderRadius: "6px",
            }}
          ></div>
          <div
            style={{
              float: "left",
              width: "100px",
              height: "70px",
              backgroundColor: "#f472b6",
              margin: "10px",
              borderRadius: "6px",
            }}
          ></div>
          <p>
            Thanks to <code>.clearfix</code>, this container correctly expands
            around its floated children.
          </p>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Clearing floats is an important concept for legacy layouts. However,
          modern CSS techniques like{" "}
          <a href="/css/flexbox" className="learn-more">
            Flexbox
          </a>{" "}
          and{" "}
          <a href="/css/grid" className="learn-more">
            CSS Grid
          </a>{" "}
          remove the need for float-based layouts.
        </p>
      </div>
    </div>
  );
};

export default Clear;
